// web/src/routes/supplements/SupplementHistory.jsx
// Every change to every shelf, newest first, each one undoable once.
//
// Taylor asked for corrections that don't lose the history, which is a
// statement about the shape of the data rather than a feature: the count is a
// sum over these rows, so an undo has to be a new row that reverses an old one
// and never an edit or a deletion. That makes this page the whole truth —
// there is no other record of what the shelf has been.
//
// So a row says what it did AND what it did it to: "12 → 11", not "-1". The
// delta is what was recorded; the pair is what a person remembers.

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { fmtStamp } from '../../lib/dates';
import { useAuth } from '../../lib/auth';
import { Panel, Empty, Rows, PlannerSkeleton } from '../../components/dashboard/Planner';
import { friendly } from './SupplementDetail';

const WORD = {
  opened: 'Opened',
  received: 'Received',
  correction: 'Counted',
  import: 'Imported',
  undo: 'Undone',
};

export default function SupplementHistory() {
  const qc = useQueryClient();
  const { activeRole } = useAuth();
  const canManage = activeRole === 'owner' || activeRole === 'manager';
  const [error, setError] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['supplement-history'],
    queryFn: () => api.get('/supplements/history?limit=100'),
  });

  const undo = useMutation({
    mutationFn: (entryId) => api.post(`/supplements/entries/${entryId}/undo`, {}),
    onMutate: () => setError(null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['supplement-history'] });
      qc.invalidateQueries({ queryKey: ['supplements'] });
      qc.invalidateQueries({ queryKey: ['supplement'] });
    },
    onError: (e) => setError(friendly(e)),
  });

  const entries = data?.entries ?? [];
  // Running totals, oldest first, so each row can show what it changed the
  // shelf FROM and TO. Worked out here rather than stored: the count is a sum
  // and a stored "balance after" column would be a second answer to the same
  // question, free to drift from the first.
  const totals = runningTotals(entries);

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto">
      <header className="flex items-end justify-between gap-3 mb-4 pb-2 border-b-2 border-hunter/30">
        <div className="min-w-0">
          <Link to="/supplements" className="text-xs text-hunter hover:underline">← Supplements</Link>
          <h1 className="text-lg md:text-xl font-semibold leading-tight">Inventory history</h1>
          <p className="text-sm text-ink-500">Every change, newest first.</p>
        </div>
      </header>

      {error && <p className="mb-3 text-sm text-rose-700">{error}</p>}

      {isLoading ? (
        <PlannerSkeleton />
      ) : (
        <Panel title="Changes" count={entries.length || null}>
          {entries.length === 0 ? (
            <Empty>Nothing has been recorded yet.</Empty>
          ) : (
            <Rows>
              {entries.map((e) => (
                <li key={e.id} className="px-3 py-2 text-sm flex items-baseline gap-3">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">
                      <Link to={`/supplements/${e.supplementId}`} className="font-medium hover:underline">
                        {e.supplementName}
                      </Link>
                      <span className="text-ink-500"> · {e.ownerName}</span>
                    </span>
                    <span className="block text-xs text-ink-400">
                      {WORD[e.reason] ?? e.reason}
                      {' · '}
                      <span className="tabular-nums">
                        {totals.get(e.id)?.from} → {totals.get(e.id)?.to}
                      </span>
                      {e.byName ? ` · ${e.byName}` : ''}
                      {' · '}
                      {fmtStamp(e.createdAt, 'short', true)}
                    </span>
                    {e.note && <span className="block text-xs text-ink-500 truncate">{e.note}</span>}
                  </span>

                  {/* Offered exactly once per row: the server refuses a second
                      undo, and a button that fails is worse than one that is
                      not there. An undo is itself a row and cannot be undone —
                      undoing it would be redoing, which is a different word
                      and a different promise. */}
                  {canManage && !e.undone && e.reason !== 'undo' ? (
                    <button
                      type="button"
                      onClick={() => undo.mutate(e.id)}
                      disabled={undo.isPending}
                      className="shrink-0 text-xs px-2 py-1 rounded border border-ink-200 text-ink-600 hover:bg-ink-50 disabled:text-ink-300"
                    >
                      Undo
                    </button>
                  ) : (
                    <span className="shrink-0 text-xs text-ink-300">
                      {e.undone ? 'undone' : ''}
                    </span>
                  )}
                </li>
              ))}
            </Rows>
          )}
        </Panel>
      )}
    </div>
  );
}

/**
 * What each entry changed the shelf from and to.
 *
 * The feed arrives newest first, so this walks it backwards accumulating per
 * (supplement, owner) shelf, then reports each row's before and after. Only
 * exact for the window that was fetched — the oldest rows on a long shelf will
 * read from a partial base — which is why the page asks for a hundred and the
 * numbers that matter are at the top where people look.
 */
function runningTotals(entries) {
  const out = new Map();
  const running = new Map();
  for (let i = entries.length - 1; i >= 0; i--) {
    const e = entries[i];
    const key = `${e.supplementId}|${e.ownerUserId}`;
    const from = running.get(key) ?? 0;
    const to = from + e.delta;
    running.set(key, to);
    out.set(e.id, { from, to });
  }
  return out;
}
